import { APIClient } from "./axiosClient";

/** Matches `getOffersOfMerchantFromDB` / createOrUpdate address line items */
export type MerchantOfferAddressDetailVO = {
    id?: number;
    address: string;
    city: string;
    country: string;
    location: string;
    phoneCountryCode: string;
    phoneNumber: string;
};

export type CreateOrUpdateMerchantOfferAddressDetailsPayload = {
    offersOfMerchantId: number;
    addressDetailsVOList: MerchantOfferAddressDetailVO[];
};

export class MerchantApi {
    static getMerchantsFromDB(filters = {}) {
        return APIClient(
            '/merchant/getMerchantsFromDB',
            filters,
            { requestType: 'post' }
        )
    }

    static getMerchantsOfOfferFromDB(filters = {}) {
        return APIClient(
            '/merchant/getMerchantsOfOfferFromDB',
            filters,
            { requestType: 'post' }
        );
    }

    static getStoresUsingMerchantId(merchantId) {
        return APIClient(`/merchant/getStoresUsingMerchantId?merchantId=${merchantId}`)
    }

    static deleteMerchantStore(merchantStoreId) {
        return APIClient(`/merchant/deleteMerchantStore?merchantStoreId=${merchantStoreId}`)
    }

    static addMerchantStore(payload) {
        return APIClient(`/merchant/createOrUpdateMerchantStore`, payload, { requestType: 'post' })
    }

    // New: Offline store creation via multipart form-data (v2 endpoint)
    static addOfflineMerchantStore(formData) {
        return APIClient(
            '/merchant/v2/createOrUpdateMerchantStore',
            formData,
            { requestType: 'post', contentType: 'multipart/form-data' }
        );
    }

    static createUpdateMerchant(data) {
        return APIClient('/merchant/createOrUpdateMerchant',
            data,
            { contentType: 'multipart/form-data', requestType: 'post' },
        );
    }

    static getAllMerchants() {
        return APIClient("/merchant/getAllMerchants");
    }

    static getMerchantOffers(payload) {
        return APIClient('/merchant/getMerchantOffersFromDB', payload, { requestType: 'post' });
    }

    static getOffersOfMerchantFromDB(payload: {
        startDateInMillis: string;
        endDateInMillis: string;
        merchantId: string;
        cashbackPercentage: string;
        sortOrder: string;
    }) {
        return APIClient(
            '/merchant/getOffersOfMerchantFromDB',
            payload,
            { requestType: 'post' }
        );
    }

    /** Public / catalog — same core engine base URL as other Offer Point merchant APIs */
    static getOffers(payload: {
        startDateInMillis: string;
        endDateInMillis: string;
        merchantId: number;
        cashbackPercentage: string;
        sortOrder: string;
    }) {
        return APIClient('/merchant/getOffers', payload, { requestType: 'post' });
    }

    /** Public program catalog — POST; programCode from public page URL query */
    static getProgramDetailsOfferPoint(payload: {
        programCode: string;
        pageNumber: number;
        pageSize: number;
        title?: string;
    }) {
        return APIClient(
            '/merchant/getProgramDetailsOfferPoint',
            {
                programCode: (payload.programCode || '').trim(),
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                title: (payload.title ?? '').trim(),
            },
            { requestType: 'post' },
        );
    }

    /** Public storefront — all offers catalog (paginated) */
    static getAllOffers(payload: {
        startDateInMillis: string | number;
        endDateInMillis: string | number;
        pageSize: number;
        pageNumber: number;
        title?: string;
        categoryId?: number | null;
        merchantId?: number | null;
    }) {
        return APIClient('/merchant/getAllOffers', payload, {
            requestType: 'post',
            withCredentials: false,
        });
    }

    /** Public storefront — merchants of offer catalog (paginated) */
    static getPublicMerchantsOfOffer(payload: {
        merchantId?: number | null;
        programId?: number | null;
        programIds?: number[];
        country?: string | null;
        merchantName?: string | null;
        pageNumber: number;
        pageSize: number;
        sortBy?: string | null;
        sortOrder?: string | null;
    }) {
        return APIClient('/merchant/getPublicMerchantsOfOffer', payload, {
            requestType: 'post',
            withCredentials: false,
        });
    }

    /** Public storefront — all categories (GET) */
    static getAllCategories() {
        return APIClient('/merchant/getAllCategories', undefined, {
            withCredentials: false,
        });
    }

    static getMerchantContacts(merchantId) {
        return APIClient(`/merchant/getMerchantContacts?merchantId=${merchantId}`);
    }

    static getMerchantAssignmentDataFromDB(payload) {
        return APIClient(
            '/merchant/getMerchantAssignmentDataFromDB',
            payload,
            { requestType: 'post' }
        );
    }

    /** Offer Point — program ↔ merchant assignment lists */
    static getMerchantOfOfferAssignmentDataFromDB(payload: {
        programId: number;
        pageNumber: number;
        pageSize: number;
        sortOrder: string;
        sortBy: string;
    }) {
        return APIClient(
            '/merchant/getMerchantOfOfferAssignmentDataFromDB',
            payload,
            { requestType: 'post' }
        );
    }

    static assignMerchantsToProgram(payload) {
        return APIClient(
            '/merchant/assignMerchantsToProgram',
            payload,
            { requestType: 'post' }
        );
    }

    static unAssignMerchantsFromProgram(assignmentIds) {
        return APIClient(
            '/merchant/unAssignMerchantsFromProgram',
            assignmentIds,
            { requestType: 'post' }
        );
    }

    static assignMerchantsOfOfferToProgramOffer(payload) {
        return APIClient(
            '/merchant/assignMerchantsOfOfferToProgramOffer',
            payload,
            { requestType: 'post' }
        );
    }

    static unAssignMerchantsOfOfferToProgramOffer(merchantIds) {
        return APIClient(
            '/merchant/unAssignMerchantsOfOfferToProgramOffer',
            merchantIds,
            { requestType: 'post' }
        );
    }

    static getCategoriesUsingMerchantId(merchantId) {
        return APIClient(`/merchant/getCategoriesUsingMerchantId?merchantId=${merchantId}`);
    }

    static createOrUpdateMerchantOffer(formData) {
        return APIClient(
            '/merchant/createOrUpdateMerchantOffer',
            formData,
            { requestType: 'post', contentType: 'multipart/form-data' }
        );
    }

    static createOrUpdateMerchantOfferForOfferPage(formData) {
        return APIClient(
            '/merchant/createOrUpdateMerchantOfferForOfferPage',
            formData,
            { requestType: 'post', contentType: 'multipart/form-data' }
        );
    }

    static deleteOffersOfMerchant(offersOfMerchantId: number | string) {
        return APIClient(
            `/merchant/deleteOffersOfMerchant?offersOfMerchantId=${offersOfMerchantId}`,
            {},
            { requestType: 'delete' }
        );
    }

    static deleteOffer(offerId: number | string) {
        return APIClient(
            `/merchant/deleteOffer?offerId=${offerId}`,
            {},
            { requestType: 'delete' }
        );
    }

    static createOrUpdateMerchantOfferAddressDetails(
        payload: CreateOrUpdateMerchantOfferAddressDetailsPayload
    ) {
        return APIClient(
            '/merchant/createOrUpdateMerchantOfferAddressDetails',
            payload,
            { requestType: 'post' }
        );
    }

    static createOrUpdateMerchantOfOfferPage(formData) {
        return APIClient(
            '/merchant/createOrUpdateMerchantOfOfferPage',
            formData,
            { requestType: 'post', contentType: 'multipart/form-data' }
        );
    }

    static createOrUpdateMerchantContact(payload) {
        return APIClient('/merchant/createOrUpdateMerchantContact', payload, { requestType: 'post' });
    }

    static createOrUpdateMerchantOfOfferContact(payload) {
        return APIClient('/merchant/createOrUpdateMerchantOfOfferContact', payload, { requestType: 'post' });
    }

    static deleteMerchantContact(merchantContactId) {
        return APIClient(`/merchant/deleteMerchantContact?merchantContactId=${merchantContactId}`);
    }

    static getMerchantDataUsingProgramId() {
        return APIClient('/merchant/getMerchantDataUsingProgramId');
    }

    static getMerchantsAnalytics(payload: {
        sortOrder: string;
        sortBy: string;
        pageNumber: number;
        pageSize: number;
        programCode: string;
        startDateInMillis?: number;
        endDateInMillis?: number;
    }) {
        return APIClient('/merchant/getMerchantsAnalytics', payload, { requestType: 'post' });
    }
}